import { useEffect, useState, useMemo } from 'react';

import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { GENERAL_INFO_TITLE, SETTINGS_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT, APISuccessT } from 'shared/models/api';
import { DeviceProfileT } from 'shared/models/device-profiles';
import SCHCTemplate from 'shared/models/SCHCTemplate';
import api from 'utils/api';

import './DeviceProfileManageData.scss';

import ManageMulticastAddress from './components/ManageMulticastAddress';
import ManageTemplateParameters from './components/ManageTemplateParameters';
import validator from './DeviceProfileManageData.validator';

export type FormValues = {
  name: string;
  isMulticast: boolean;
  ipMulticastAddress: string;
  templateId: string;
  sCHCTemplateID?: string;
  templateName: string;
  autoProvision: boolean;
  dropNonMatching: boolean;
  flushDownlinkQueue: boolean;
  templateParameters: Array<SCHCTemplate.API.ParametersT>;
}

function DeviceProfileManageData() {
  const [defaultFormValues, setDefaultFormValues] = useState<FormValues>({
    name: '',
    isMulticast: false,
    ipMulticastAddress: '',
    templateId: '',
    templateName: '',
    autoProvision: false,
    dropNonMatching: false,
    flushDownlinkQueue: false,
    templateParameters: [],
  });
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: useMemo(() => defaultFormValues, [defaultFormValues]),
    resolver: validator,
  });
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: {
      errors,
    },
  } = methods;
  const history = useHistory();
  const { flowId, deviceProfileId } = useParams() as any;
  const isEdit = deviceProfileId !== undefined;
  const [isLoading, setIsLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      api.deviceProfiles.getOne(deviceProfileId, { flowId })
        .then((resp: any) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                toast.error(error?.message);
                history.push(`/flows/${flowId}`);
              });
          } else {
            resp.json()
              .then((existingData: DeviceProfileT) => {
                const newValues = {
                  ...existingData,
                  buffering: `${existingData.buffering}`,
                };
                setDefaultFormValues(newValues);
                reset(newValues);
                setIsLoading(false);
              });
          }
        });
    }
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (formData: FormValues) => {
    const request = isEdit === false
      ? api.deviceProfiles.register(formData, { flowId })
      : api.deviceProfiles.update(deviceProfileId, formData, { flowId });
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
        } else {
          resp.json()
            .then((data: APISuccessT) => {
              toast.success(`Device Profile has been ${isEdit ? 'edited' : 'created'}`);
              history.push(`/flows/${flowId}/device-profiles/${isEdit ? deviceProfileId : data.id}`);
            });
        }
      });
  };

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <FormProvider {...methods}>
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="title__header">
          <h1>Device Profile · { isEdit ? 'Edit' : 'Create' }</h1>
          <ButtonSubmitData isEdit={isEdit} />
        </div>
        <div className="scroll">
          <div className="form__section">
            <h3>{ GENERAL_INFO_TITLE }</h3>
            <InputForm.TextField
              fullWidth
              sx={{ m: 1 }}
              label="Name"
              register={register('name')}
              errorField={errors.name}
            />
          </div>
          <div className="form__section">
            <h3>{ SETTINGS_TITLE }</h3>
            <ManageMulticastAddress />
            <InputForm.Checkbox
              name="autoProvision"
              control={control}
              label="Auto Provisioning"
              sx={{ m: 1 }}
              defaultChecked={defaultFormValues.autoProvision}
              color="primary"
              helpText="Automatically provisions new devices in this device profile on the first uplink packet reception"
            />
            <InputForm.Checkbox
              name="dropNonMatching"
              control={control}
              label="Drop Non-Matching"
              sx={{ m: 1 }}
              defaultChecked={defaultFormValues.dropNonMatching}
              color="primary"
              helpText="Drops SCHC uplink packets with non-matching rules"
            />
            <InputForm.Checkbox
              name="flushDownlinkQueue"
              control={control}
              label="Flush Dowlink-Queue"
              sx={{ m: 1 }}
              defaultChecked={defaultFormValues.flushDownlinkQueue}
              color="primary"
              helpText="Flush downlink queue at the LPWAN NS"
            />
            <ManageTemplateParameters />
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

export default DeviceProfileManageData;
